import type { DoczFragment, FragmentMark, FragmentRun } from "@norkron/web-docz";

/**
 * `markdownFragment` — a LEGACY `markdown` comment body as a real docz
 * `DoczFragment`, so it paints on the same canvas surface every other comment
 * body does.
 *
 * ## The bug it closes
 *
 * Owner, 2026-07-30: *"why the fuck are review note replies STILL in DOM/HTML?!"*
 *
 * The cause is NOT the nesting and NOT the reply component: `<CommentBody>`
 * dispatches on `body_format`, and both a root and a reply go through the same
 * two branches. What differed was the DATA. MEASURED on production `a9c50c0`,
 * doc 11: every reply the owner was looking at (`reply-11`, "P0-FIX reply
 * 1784833740414") is stored `body_format: "markdown"` and fell down the
 * `renderMarkdown` → React-nodes branch — `data-comment-surface` absent, no
 * surface, no engine — while the docz-fragment replies beside it
 * (`reply-135/136/137`) reported `canvas`. Two roots in the same document
 * (`P0-FIX create`, `W3 verify`) are markdown too and were equally DOM; the
 * owner reads it as "replies" because that is where the legacy corpus sits.
 *
 * ⇒ There is now ONE painter. A legacy markdown body is converted to the same
 * single-block fragment shape the composer stores and handed to the same
 * `<CommentCanvasBody>`. No second paint path, no fork of the surface, no branch
 * in the component beyond "decode the stored form".
 *
 * ## Why converting is legitimate rather than a lossy hack
 *
 * `CommentComposer.seedFragment` ALREADY does this whenever an author edits a
 * legacy note (`textFragment(body)`): the migration path from markdown to IR is
 * shipped product behaviour, not an invention here. This is the same direction,
 * with the inline formatting preserved instead of flattened to plain text —
 * `**bold**` becomes a real docz `bold` MARK, which is strictly MORE faithful
 * than what a one-run `textFragment` would have produced.
 *
 * ## ⛔ What a fragment structurally cannot carry, stated rather than hidden
 *
 * A `DoczFragment` is SINGLE-BLOCK by design (one `kind`, one flat run list) and
 * three call sites depend on that — `commentActivatableRanges` hard-codes
 * `blockOrd: 0`, `mountCommentSurface` calls `rangeRects(0, …)`,
 * `commentMirrorBlocks` emits one `ord: 0` — because that byte coordinate is the
 * one `el.comment_anchors` speaks. Splitting a body into several blocks to carry
 * `<ul>`/`<blockquote>` semantics would break all three (see the note in
 * `commentBreaks.test.ts`).
 *
 * So block structure is carried as TEXT, and only in the one form that invents
 * nothing: the author's own source marker is kept verbatim (`- item`, `1. item`,
 * `> quoted`) and the blocks are joined with `\n`. pdfz splits a `\n` inside a
 * run into line boxes (`flex.zig:6604`), so the PAINTED result keeps the breaks.
 *
 * ⚠️ RESIDUAL, and it is not this module's: the ACCESSIBLE MIRROR still collapses
 * those newlines, because `@norkron/web-docz`'s shipped `wasm/docz.wasm` predates
 * docz `b9014b0` and emits no `<br>` at all. That is the open comment-line-break
 * bug and it affects canvas-composed bodies identically today — converting a
 * markdown body neither causes it nor makes it worse.
 *
 * ## Security
 *
 * The old path was safe because React escapes every text node. This path is safe
 * for a stronger reason: it produces IR, and the glyphs are painted from IR
 * through wasm with no HTML in the path at all. The one attacker-reachable field
 * that survives as a live control is a link's `href`, so {@link safeHref} keeps
 * the ORIGINAL allow-list byte-for-byte (`http`, `https`, `mailto` only) and a
 * rejected scheme degrades to the link's plain text — exactly as before. The
 * accessible mirror is still built by `commentMirrorBlocks`, which runs
 * `sanitizeCommentHtml` first; that boundary is untouched.
 */

/** Only these link schemes may become a real `link` mark; anything else renders
 *  as the plain text it wrapped. Byte-identical to `lib/markdown.tsx`'s policy —
 *  the same strings reach the same allow-list, so the migration cannot widen it. */
function safeHref(url: string): string | null {
  const u = url.trim();
  if (/^https?:\/\//i.test(u) || /^mailto:[^\s]+$/i.test(u)) return u;
  return null;
}

/** One tokenized piece of inline markdown: literal text plus the marks in force
 *  over it. The flat shape IS the fragment run shape, which is the point —
 *  nothing downstream has to re-walk a tree. */
interface Span {
  readonly text: string;
  readonly marks: readonly FragmentMark[];
}

interface InlinePattern {
  readonly re: RegExp;
  /**
   * The token's content is LITERAL — do not keep tokenizing inside it.
   *
   * ⚠️ This mirrors the DOM renderer EXACTLY, and the difference is visible:
   * `lib/markdown.tsx` recurses (`formatInline`) inside `**` and `*`/`_`, and
   * does NOT inside `` ` `` or a link's text (it emits `{m[1]}` raw). So
   * `` `**x**` `` is the four literal characters `**x**` set in code, not a bold
   * `x`. Getting this wrong silently re-interprets stored bodies — the test
   * "inline code is LITERAL" is what caught it here.
   */
  readonly literal?: boolean;
  /** The inner source, and the mark this token adds (`null` for a link whose
   *  scheme was rejected — the text survives, the mark does not). */
  readonly apply: (m: RegExpExecArray) => { inner: string; mark: FragmentMark | null };
}

/**
 * The inline grammar, IN THE SAME ORDER AND WITH THE SAME REGEXES as the DOM
 * renderer it replaces. Identical patterns are not a coincidence to be tidied:
 * every stored legacy body was authored against these, so a "cleaner" regex here
 * would silently re-interpret comments that are already in the database.
 */
const INLINE: readonly InlinePattern[] = [
  // Code FIRST — its content is literal, so `` `**x**` `` must not become bold.
  {
    re: /`([^`]+)`/,
    literal: true,
    apply: (m) => ({ inner: m[1] ?? "", mark: { type: "code" } }),
  },
  { re: /\*\*([\s\S]+?)\*\*/, apply: (m) => ({ inner: m[1] ?? "", mark: { type: "bold" } }) },
  { re: /\*([^*\n]+?)\*/, apply: (m) => ({ inner: m[1] ?? "", mark: { type: "italic" } }) },
  { re: /_([^_\n]+?)_/, apply: (m) => ({ inner: m[1] ?? "", mark: { type: "italic" } }) },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    literal: true,
    apply: (m) => {
      const href = safeHref(m[2] ?? "");
      return {
        inner: m[1] ?? "",
        mark: href === null ? null : { type: "link", href },
      };
    },
  },
];

/** The earliest inline token in `text`, or null. Earliest WINS, so the pattern
 *  order above only breaks ties at the same index (code before emphasis). */
function firstInline(
  text: string,
): {
  index: number;
  length: number;
  inner: string;
  mark: FragmentMark | null;
  literal: boolean;
} | null {
  let best: {
    index: number;
    length: number;
    inner: string;
    mark: FragmentMark | null;
    literal: boolean;
  } | null = null;
  for (const p of INLINE) {
    const m = p.re.exec(text);
    if (!m) continue;
    if (best !== null && m.index >= best.index) continue;
    const { inner, mark } = p.apply(m);
    best = {
      index: m.index,
      length: m[0].length,
      inner,
      mark,
      literal: p.literal === true,
    };
  }
  return best;
}

/**
 * Tokenize inline markdown into flat spans, carrying `inherited` down into every
 * nested token — `**bold with a [link](https://x.test)**` yields a span holding
 * BOTH marks, which is what the docz run model wants and what a DOM tree gets for
 * free from nesting.
 */
function inlineSpans(
  text: string,
  inherited: readonly FragmentMark[] = [],
  depth = 0,
): Span[] {
  // A cheap recursion fence. The grammar is non-left-recursive (every step
  // consumes the delimiters), so this can only be reached by pathological input;
  // stopping produces the literal text rather than a blown stack.
  if (depth > 8) return text === "" ? [] : [{ text, marks: inherited }];
  const out: Span[] = [];
  let rest = text;
  for (;;) {
    const hit = firstInline(rest);
    if (!hit) {
      if (rest !== "") out.push({ text: rest, marks: inherited });
      return out;
    }
    if (hit.index > 0) out.push({ text: rest.slice(0, hit.index), marks: inherited });
    const marks = hit.mark === null ? inherited : [...inherited, hit.mark];
    if (hit.literal) {
      if (hit.inner !== "") out.push({ text: hit.inner, marks });
    } else {
      out.push(...inlineSpans(hit.inner, marks, depth + 1));
    }
    rest = rest.slice(hit.index + hit.length);
  }
}

/**
 * The source's block lines, each kept with the marker the author typed.
 *
 * ⛔ NOT re-grouped into `<ul>`/`<blockquote>` structures: a fragment has no
 * second block to put them in (see the module note). Keeping `- `, `1. ` and
 * `> ` verbatim means the reviewer reads what was written and the reviewer's
 * clipboard — which is sliced from this IR at byte offsets — carries the same
 * bytes the database does. Inventing a `•` would put a glyph nobody typed into
 * both.
 */
function blockLines(source: string): string[] {
  const out: string[] = [];
  for (const raw of source.replace(/\r\n/g, "\n").split("\n")) {
    out.push(raw.replace(/\s+$/, ""));
  }
  // Trailing blank lines are trimmed: they would paint as empty line boxes at
  // the bottom of the card, which reads as a rendering fault.
  while (out.length > 0 && out[out.length - 1] === "") out.pop();
  return out;
}

/** Adjacent spans carrying the SAME marks merged into one run — fewer runs is
 *  fewer shaped runs in the layout pass, and the byte offsets are unchanged. */
function coalesce(spans: readonly Span[]): FragmentRun[] {
  const runs: FragmentRun[] = [];
  for (const s of spans) {
    if (s.text === "") continue;
    const last = runs[runs.length - 1];
    if (last && sameMarks(last.marks, s.marks)) {
      runs[runs.length - 1] = { text: last.text + s.text, marks: last.marks };
      continue;
    }
    runs.push({ text: s.text, marks: [...s.marks] });
  }
  return runs;
}

function sameMarks(a: readonly FragmentMark[], b: readonly FragmentMark[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x === undefined || y === undefined) return false;
    if (x.type !== y.type) return false;
    if (x.type === "link" && y.type === "link" && x.href !== y.href) return false;
  }
  return true;
}

/**
 * A legacy `markdown` comment body as a single-block docz fragment.
 *
 * An empty / whitespace-only body yields a fragment with NO runs — the caller
 * must treat that as "nothing to paint" rather than mounting a blank sheet, the
 * same rule `quoteBlocks` follows.
 */
export function markdownFragment(source: string): DoczFragment {
  const lines = blockLines(source);
  const spans: Span[] = [];
  lines.forEach((line, i) => {
    if (i > 0) spans.push({ text: "\n", marks: [] });
    // ⛔ Mentions are NOT tokenized here, deliberately. A mention chip is painted
    // from the byte range of a literal `@<recorded name>` by
    // `commentMentionTargets`, scanned off the fragment the surface receives —
    // exactly as it is for a composer-authored body. Turning it into a mark here
    // would give the canvas two different notions of what a chip is.
    spans.push(...inlineSpans(line));
  });
  return { kind: "paragraph", runs: coalesce(spans) };
}
